import { useCallback, useEffect, useRef, useState } from 'react'

// Runs student Python in Pyodide inside a Web Worker. Unlike the main-thread runner the
// classroom IDE uses (workspace/hooks/useCodeRunner.js), a worker can be terminated, so an
// infinite loop in a student's function times out instead of freezing the whole page -
// which matters here because hidden tests run automatically right after submission.
//
// Same Pyodide version/CDN as the rest of the app.
const PYODIDE_URL = 'https://cdn.jsdelivr.net/pyodide/v0.23.4/full/'
const RUN_TIMEOUT_MS = 20000
const TESTS_TIMEOUT_MS = 30000
// Extra time before a request's clock starts, for downloading imported packages.
const PACKAGE_LOAD_ALLOWANCE_MS = 90000

const WORKER_SOURCE = `
importScripts('${PYODIDE_URL}pyodide.js');
const ready = loadPyodide({ indexURL: '${PYODIDE_URL}' });
ready.then(() => self.postMessage({ type: 'ready' }), (e) => self.postMessage({ type: 'load_error', error: String(e) }));

// Calls the student's function on each hidden input and normalizes every return value to a
// flat JSON list of floats, so tuples, nested lists, numpy arrays and numpy scalars all
// compare the same way server-side. Each input carries its call arguments (args); inputs
// from before multi-problem support only have x and y.
const HARNESS = \`
import copy as __rg_copy
import json as __rg_json
def __rg_flat(value):
    if hasattr(value, 'tolist'):
        value = value.tolist()
    if isinstance(value, (list, tuple)):
        return [item for part in value for item in __rg_flat(part)]
    return [float(value)]
def __rg_run(inputs_json, fn_name):
    out = []
    fn = globals().get(fn_name)
    for t in __rg_json.loads(inputs_json):
        if not callable(fn):
            out.append({'id': t['id'], 'error': fn_name + ' is not defined'})
            continue
        try:
            args = t['args'] if 'args' in t else [t['x'], t['y']]
            r = fn(*__rg_copy.deepcopy(args))
            out.append({'id': t['id'], 'output': __rg_flat(r)})
        except Exception as e:
            out.append({'id': t['id'], 'error': type(e).__name__ + ': ' + str(e)[:300]})
    return __rg_json.dumps(out)
\`;

self.onmessage = async (event) => {
  const { id, kind, code, inputs, functionName } = event.data;
  let stdout = '';
  try {
    const py = await ready;
    py.setStdout({ batched: (s) => { stdout += s + '\\n'; } });
    py.setStderr({ batched: (s) => { stdout += s + '\\n'; } });
    await py.loadPackagesFromImports(code);
    // Packages (numpy is ~2.5MB) are downloaded now; the time limit should only cover the
    // student's own code, so tell the page to restart its clock.
    self.postMessage({ id, type: 'loaded' });
    const ns = py.globals.get('dict')();
    // 'run' executes the student's own __main__ block (the starter prints the example);
    // 'tests' must not, so the hidden-test output is not mixed with their prints.
    ns.set('__name__', kind === 'run' ? '__main__' : '__student__');
    await py.runPythonAsync(code, { globals: ns });
    if (kind === 'tests') {
      await py.runPythonAsync(HARNESS, { globals: ns });
      const json = ns.get('__rg_run')(JSON.stringify(inputs), functionName);
      self.postMessage({ id, ok: true, stdout, results: JSON.parse(json) });
    } else {
      self.postMessage({ id, ok: true, stdout });
    }
    ns.destroy();
  } catch (e) {
    self.postMessage({ id, ok: false, stdout, error: String(e && e.message ? e.message : e) });
  }
};
`

// Shortens Pyodide tracebacks to the part a student can act on (their own file's frames).
export function cleanPythonError (message) {
  if (!message) return ''
  const lines = String(message).split('\n')
  const start = lines.findIndex(l => l.includes('File "<exec>"'))
  return (start >= 0 ? ['Traceback (most recent call last):', ...lines.slice(start)] : lines).join('\n').trim()
}

export default function usePyodideWorker () {
  const workerRef = useRef(null)
  const readyRef = useRef(null) // Promise<boolean> for the current worker's Pyodide load
  const pendingRef = useRef(new Map())
  const nextIdRef = useRef(1)
  const [status, setStatus] = useState('loading') // loading | ready | error

  const spawn = useCallback((attempt = 1) => {
    const url = URL.createObjectURL(new Blob([WORKER_SOURCE], { type: 'text/javascript' }))
    const worker = new Worker(url)
    URL.revokeObjectURL(url)
    setStatus('loading')
    let settle
    readyRef.current = new Promise((resolve) => { settle = resolve })
    // A cold CDN fetch occasionally fails the worker's importScripts; retry once with a
    // fresh worker before telling the student Python is unavailable.
    const failLoad = () => {
      worker.terminate()
      if (attempt < 2) {
        setTimeout(() => {
          spawn(attempt + 1)
          readyRef.current.then(settle)
        }, 1000)
      } else {
        setStatus('error')
        settle(false)
      }
    }
    worker.onmessage = (event) => {
      const msg = event.data
      if (msg.type === 'ready') { setStatus('ready'); settle(true); return }
      if (msg.type === 'load_error') { failLoad(); return }
      if (msg.type === 'loaded') {
        // Package downloads finished: restart this request's clock (see send).
        const waiting = pendingRef.current.get(msg.id)
        if (waiting) { clearTimeout(waiting.timer); waiting.timer = setTimeout(waiting.onTimeout, waiting.timeoutMs) }
        return
      }
      const pending = pendingRef.current.get(msg.id)
      if (!pending) return
      clearTimeout(pending.timer)
      pendingRef.current.delete(msg.id)
      pending.resolve(msg)
    }
    worker.onerror = (event) => {
      // Handled here - don't let it surface as an uncaught page error.
      event.preventDefault()
      failLoad()
    }
    workerRef.current = worker
  }, [])

  useEffect(() => {
    spawn()
    const pending = pendingRef.current
    return () => {
      workerRef.current && workerRef.current.terminate()
      pending.forEach(p => clearTimeout(p.timer))
      pending.clear()
    }
  }, [spawn])

  const send = useCallback(async (message, timeoutMs) => {
    // Wait for Pyodide itself before starting the clock: a slow CDN load is not the
    // student's infinite loop.
    const loaded = await readyRef.current
    if (!loaded) return { ok: false, loadError: true, stdout: '', error: 'The Python runtime could not be loaded. Check your connection and reload the page.' }
    return new Promise((resolve) => {
      const id = nextIdRef.current++
      const onTimeout = () => {
        pendingRef.current.delete(id)
        // Kill the stuck interpreter and start a fresh one for the next run.
        workerRef.current && workerRef.current.terminate()
        spawn()
        resolve({ id, ok: false, timedOut: true, stdout: '', error: `Timed out after ${timeoutMs / 1000}s - check for an infinite loop.` })
      }
      // Generous first window covers downloading packages (numpy on slow classroom Wi-Fi);
      // once the worker reports them loaded, the clock restarts at timeoutMs for the code itself.
      const timer = setTimeout(onTimeout, timeoutMs + PACKAGE_LOAD_ALLOWANCE_MS)
      pendingRef.current.set(id, { resolve, timer, onTimeout, timeoutMs })
      workerRef.current.postMessage({ id, ...message })
    })
  }, [spawn])

  const run = useCallback(code => send({ kind: 'run', code }, RUN_TIMEOUT_MS), [send])
  const runTests = useCallback((code, inputs, functionName) =>
    send({ kind: 'tests', code, inputs, functionName }, TESTS_TIMEOUT_MS), [send])

  return { status, run, runTests }
}
